import { Ant, AntRole, ScentType } from '../Ant';
import { TunnelNetwork, Chamber } from '../TunnelNetwork';
import { advanceTimer } from '../../SimClock';

/**
 * Context required for metabolism operations.
 */
export interface MetabolismContext {
  tunnelNetwork: TunnelNetwork;
  ants: Map<number, Ant>;
  antsOnSurface: Set<number>;
  queen: Ant | null;
  queenHunger: number;
  killAnt: (ant: Ant, cause: string) => void;
}

/**
 * Configuration for the metabolism system.
 */
export interface MetabolismConfig {
  METABOLISM_INTERVAL: number;
  WORKER_FOOD_RATE: number;
  QUEEN_FOOD_RATE: number;
  ANT_HUNGER_RATE: number;
  ANT_HUNGER_MAX: number;
  ANT_HUNGER_FEED_THRESHOLD: number;
  FORAGE_FOOD_THRESHOLD: number;
}

export const DEFAULT_METABOLISM_CONFIG: MetabolismConfig = {
  METABOLISM_INTERVAL: 1000,       // Every second
  WORKER_FOOD_RATE: 0.008,         // Food per worker per second
  QUEEN_FOOD_RATE: 0.2,            // Queen eats 25x more than a worker
  ANT_HUNGER_RATE: 2,              // Hunger increase per second when not fed
  ANT_HUNGER_MAX: 100,
  ANT_HUNGER_FEED_THRESHOLD: 50,   // When to seek food
  FORAGE_FOOD_THRESHOLD: 0.4,      // When food < 40% capacity, more foraging
};

/**
 * MetabolismSystem handles colony food consumption and hunger.
 */
export class MetabolismSystem {
  private config: MetabolismConfig;
  private metabolismTimer = 0;

  constructor(config: Partial<MetabolismConfig> = {}) {
    this.config = { ...DEFAULT_METABOLISM_CONFIG, ...config };
  }

  /**
   * Update metabolism. Call this every tick with dt in sim-milliseconds.
   *
   * Every quantity below is per metabolism interval, not per tick, so the
   * colony eats at the same rate whatever cadence the simulation runs at —
   * provided the accumulator carries its remainder rather than zeroing, which
   * is what `advanceTimer` is for.
   */
  update(dt: number, ctx: MetabolismContext): void {
    const t = advanceTimer(this.metabolismTimer, dt, this.config.METABOLISM_INTERVAL);
    this.metabolismTimer = t.value;
    for (let i = 0; i < t.fires; i++) {
      this.processColonyMetabolism(ctx);
    }
  }

  /**
   * Process colony food consumption for one metabolism tick.
   */
  private processColonyMetabolism(ctx: MetabolismContext): void {
    const totalFood = ctx.tunnelNetwork.getTotalFood();
    const storages = ctx.tunnelNetwork.getFoodStorages();
    if (storages.length === 0) return;

    // Count living ants (not on surface)
    let workerCount = 0;
    for (const ant of ctx.ants.values()) {
      if (!ant.isAlive()) continue;
      if (ctx.antsOnSurface.has(ant.id)) continue;
      if (ant.role !== AntRole.QUEEN) {
        workerCount++;
      }
    }

    // Calculate total consumption this tick
    let totalConsumption = workerCount * this.config.WORKER_FOOD_RATE;
    if (ctx.queen && ctx.queen.isAlive()) {
      totalConsumption += this.config.QUEEN_FOOD_RATE;
    }

    // Withdraw food from storage chambers proportionally
    if (totalConsumption > 0 && totalFood > 0) {
      const actualConsumption = Math.min(totalConsumption, totalFood);

      for (const storage of storages) {
        if (storage.destroyed) continue;
        const storagePortion = (storage.foodStored ?? 0) / totalFood;
        const amountToWithdraw = actualConsumption * storagePortion;
        storage.foodStored = Math.max(0, (storage.foodStored ?? 0) - amountToWithdraw);
      }

      // If we couldn't consume enough, increase hunger for all ants (except queen)
      if (actualConsumption < totalConsumption) {
        const hungerIncrease = (totalConsumption - actualConsumption) / workerCount * 10;
        for (const ant of ctx.ants.values()) {
          if (!ant.isAlive()) continue;
          if (ctx.antsOnSurface.has(ant.id)) continue;
          if (ant.role === AntRole.QUEEN) continue;
          ant.hunger = Math.min(this.config.ANT_HUNGER_MAX, ant.hunger + hungerIncrease);
        }
      } else {
        // Slowly reduce hunger when food is available
        for (const ant of ctx.ants.values()) {
          if (!ant.isAlive()) continue;
          if (ctx.antsOnSurface.has(ant.id)) continue;
          if (ant.role === AntRole.QUEEN) continue;
          ant.hunger = Math.max(0, ant.hunger - 1);
        }
      }
    } else if (totalFood <= 0) {
      // No food - everyone gets hungry (except queen)
      for (const ant of ctx.ants.values()) {
        if (!ant.isAlive()) continue;
        if (ctx.antsOnSurface.has(ant.id)) continue;
        if (ant.role === AntRole.QUEEN) continue;
        ant.hunger = Math.min(this.config.ANT_HUNGER_MAX, ant.hunger + this.config.ANT_HUNGER_RATE);
      }
    }
  }

  /**
   * Get the foraging urgency based on colony food levels.
   * Returns a value where:
   *   - Positive = need to forage (higher = more urgent)
   *   - Negative = should stay home
   *   - Zero = neutral
   */
  getForagingUrgency(ctx: MetabolismContext): number {
    const totalFood = ctx.tunnelNetwork.getTotalFood();
    const storages = ctx.tunnelNetwork.getFoodStorages();

    // Calculate max capacity
    let maxCapacity = 0;
    for (const storage of storages) {
      if (!storage.destroyed) {
        maxCapacity += 3000; // STORAGE_CAPACITY from FoodManager
      }
    }

    if (maxCapacity === 0) return 1;
    const fillRatio = totalFood / maxCapacity;

    // Biologically plausible: foragers sense food smell at entrance chambers
    // High food smell = colony is well-fed = no need to forage
    const entrances = ctx.tunnelNetwork.getEntrances();
    let avgFoodSmell = 0;
    for (const entrance of entrances) {
      avgFoodSmell += entrance.scents?.[ScentType.FOOD_SMELL] ?? 0;
    }
    if (entrances.length > 0) {
      avgFoodSmell /= entrances.length;
    }

    // Combine fill ratio and scent signals
    // When food smell is strong (>0.5), urgency drops significantly
    const scentSuppression = Math.min(1, avgFoodSmell * 2); // 0.5 smell = full suppression

    // Below threshold, urgency increases
    if (fillRatio < this.config.FORAGE_FOOD_THRESHOLD) {
      const baseUrgency = 1 - (fillRatio / this.config.FORAGE_FOOD_THRESHOLD);
      return baseUrgency * (1 - scentSuppression * 0.5); // Scent can reduce urgency by 50%
    }

    // Above threshold, check if foraging should be suppressed
    // When food is >70% full and smell is high, foragers stay home
    if (fillRatio > 0.7 && avgFoodSmell > 0.3) {
      return -0.5; // Negative urgency = stay home signal
    }

    return 0;
  }

  /**
   * Get the metabolism timer value (for serialization).
   */
  getMetabolismTimer(): number {
    return this.metabolismTimer;
  }

  /**
   * Set the metabolism timer value (for deserialization).
   */
  setMetabolismTimer(value: number): void {
    this.metabolismTimer = value;
  }

  /**
   * Get current config.
   */
  getConfig(): MetabolismConfig {
    return { ...this.config };
  }

  /**
   * Update config with partial values (for live settings changes).
   */
  updateConfig(partial: Partial<MetabolismConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  /**
   * Reset the system state.
   */
  reset(): void {
    this.metabolismTimer = 0;
  }
}
